import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useServicePricingService from '../../app/services/hooks/useServicePricingService';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import InputField from '../../components/InputField';
import TextAreaField from '../../components/TextAreaField';
import SelectTwo from '../../components/SelectTwo';
import ButtonSaveForm from '../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../components/buttons/ButtonCancelForm';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';

interface FormData {
    service_type: string;
    plan_name: string;
    base_price: string;
    currency: string;
    description: string;
    is_default: boolean;
    features: string[];
    exclusions: string[];
}

const ServicePricingForm: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const history = useHistory();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const {
        editServicePricing,
        updateServicePricing,
        storeServicePricing,
        fetchingEditServicePricing,
        fetchingUpdateServicePricing,
        fetchingStoreServicePricing
    } = useServicePricingService();
    
    const isEditing = Boolean(id);
    
    const [formData, setFormData] = useState<FormData>({
        service_type: 'brand_verification',
        plan_name: '',
        base_price: '',
        currency: 'CLP',
        description: '',
        is_default: false,
        features: [],
        exclusions: []
    });
    
    const [loading, setLoading] = useState(false);
    const [newFeature, setNewFeature] = useState('');
    const [newExclusion, setNewExclusion] = useState('');
    
    useEffect(() => {
        if (id) {
            setLoading(true);
            editServicePricing(Number(id), {
                onSuccess: (res: any) => {
                    const pricing = res.data.service_pricing;
                    setFormData({
                        service_type: pricing.service_type || 'brand_verification',
                        plan_name: pricing.plan_name || '',
                        base_price: pricing.base_price ? pricing.base_price.toString() : '',
                        currency: pricing.currency || 'CLP',
                        description: pricing.description || '',
                        is_default: pricing.is_default || false,
                        features: pricing.features || [],
                        exclusions: pricing.exclusions || []
                    });
                    setLoading(false);
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al cargar el precio');
                    setLoading(false);
                    history.push('/intranet/service-pricing');
                }
            });
        }
    }, [id]);
    
    const handleInputChange = (field: keyof FormData) => (value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Funciones para manejar features
    const handleAddFeature = () => {
        if (newFeature.trim()) {
            setFormData(prev => ({
                ...prev,
                features: [...prev.features, newFeature.trim()]
            }));
            setNewFeature('');
        }
    };

    const handleRemoveFeature = (index: number) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const handleFeatureKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddFeature();
        }
    };

    // Funciones para manejar exclusiones
    const handleAddExclusion = () => {
        if (newExclusion.trim()) {
            setFormData(prev => ({
                ...prev,
                exclusions: [...prev.exclusions, newExclusion.trim()]
            }));
            setNewExclusion('');
        }
    };

    const handleRemoveExclusion = (index: number) => {
        setFormData(prev => ({
            ...prev,
            exclusions: prev.exclusions.filter((_, i) => i !== index)
        }));
    };

    const handleExclusionKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddExclusion();
        }
    };
    
    const handleSubmit = () => {
        // Validar que el precio sea un número
        if (formData.base_price && isNaN(Number(formData.base_price))) {
            toast.error('El precio base debe ser un número válido');
            return;
        }
        
        const submitData = {
            ...formData,
            base_price: Number(formData.base_price)
        };
        
        if (isEditing) {
            updateServicePricing(Number(id), submitData, {
                onSuccess: (res: any) => {
                    toast.success(res.message || 'Precio actualizado exitosamente');
                    setErrors({});
                    history.push('/intranet/service-pricing');
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al actualizar el precio');
                },
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Por favor corrige los errores en el formulario');
                }
            });
        } else {
            storeServicePricing(submitData, {
                onSuccess: (res: any) => {
                    toast.success(res.message || 'Precio creado exitosamente');
                    setErrors({});
                    history.push('/intranet/service-pricing');
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al crear el precio');
                },
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Por favor corrige los errores en el formulario');
                }
            });
        }
    };
    
    const handleCancel = () => {
        history.push('/intranet/service-pricing');
    };
    
    // Opciones de tipo de servicio (por ahora solo registro de marca)
    const serviceTypeOptions = [
        { value: 'brand_verification', label: 'Registro de Marca' }
    ];
    
    // Opciones de moneda (por ahora solo CLP)
    const currencyOptions = [
        { value: 'CLP', label: 'CLP - Peso Chileno' }
    ];
    
    const isSaving = fetchingUpdateServicePricing || fetchingStoreServicePricing;
    
    // Mostrar loading mientras se carga el precio para editar
    if (loading || fetchingEditServicePricing) {
        return (
            <div className="container-fluid">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', path: '#' },
                    { label: 'Precios de Servicios', path: '/intranet/service-pricing' },
                    { label: isEditing ? 'Editar' : 'Crear', isCurrent: true }
                ]}
                title={isEditing ? "Editar Plan de Servicio" : "Crear Plan de Servicio"}
                description={isEditing 
                    ? "Actualizar información del plan de precio" 
                    : "Configurar un nuevo plan de precio para un servicio"
                }
            />
            
            <DefaultCard>
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="row">
                        {/* Tipo de Servicio */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">
                                Tipo de Servicio <span className="text-danger">*</span>
                            </label>
                            <SelectTwo
                                inputValue={formData.service_type}
                                onChange={(option) =>
                                    setFormData(prev => ({ ...prev, service_type: option?.value || 'brand_verification' }))
                                }
                                options={serviceTypeOptions}
                                isClearable={false}
                                placeholder="Seleccione el tipo de servicio"
                            />
                            {fieldErrorMessage('service_type') && (
                                <small className="text-danger">{fieldErrorMessage('service_type')}</small>
                            )}
                            <small className="text-muted d-block mt-1">
                                Selecciona a qué servicio pertenece este plan de precio
                            </small>
                        </div>
                        
                        {/* Nombre del Plan */}
                        <div className="col-md-6 mb-3">
                            <InputField
                                label="Nombre del Plan"
                                value={formData.plan_name}
                                onChange={handleInputChange('plan_name')}
                                onFocus={() => onFocusRemove('plan_name')}
                                error={fieldErrorMessage('plan_name')}
                                placeholder="Ej: Básico, Estándar, Premium"
                                required
                            />
                            <small className="text-muted">
                                Nombre identificador del plan (ej: Básico, Estándar, Premium)
                            </small>
                        </div>
                        
                        {/* Precio Base */}
                        <div className="col-md-4 mb-3">
                            <InputField
                                label="Precio Base"
                                type="number"
                                value={formData.base_price}
                                onChange={handleInputChange('base_price')}
                                onFocus={() => onFocusRemove('base_price')}
                                error={fieldErrorMessage('base_price')}
                                placeholder="0"
                                required
                            />
                            <small className="text-muted">Solo números sin puntos ni comas</small>
                        </div>
                        
                        {/* Moneda */}
                        <div className="col-md-4 mb-3">
                            <label className="form-label">
                                Moneda <span className="text-danger">*</span>
                            </label>
                            <SelectTwo
                                inputValue={formData.currency}
                                onChange={(option) =>
                                    setFormData(prev => ({ ...prev, currency: option?.value || 'CLP' }))
                                }
                                options={currencyOptions}
                                isClearable={false}
                                placeholder="Seleccione moneda"
                            />
                            {fieldErrorMessage('currency') && (
                                <small className="text-danger">{fieldErrorMessage('currency')}</small>
                            )}
                        </div>
                        
                        {/* Es plan por defecto */}
                        <div className="col-md-4 mb-3 d-flex align-items-center">
                            <div className="form-check mt-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="isDefault"
                                    checked={formData.is_default}
                                    onChange={(e) => handleInputChange('is_default')(e.target.checked)}
                                    style={{ 
                                        width: '1.5rem', 
                                        height: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                />
                                <label 
                                    className="form-check-label ms-2" 
                                    htmlFor="isDefault"
                                    style={{ cursor: 'pointer', fontWeight: 500 }}
                                >
                                    Plan por defecto
                                    {formData.is_default && (
                                        <span className="badge bg-primary ms-2">Preseleccionado</span>
                                    )}
                                </label>
                                <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    Este plan vendrá seleccionado automáticamente
                                </div>
                            </div>
                        </div>
                        
                        {/* Descripción */}
                        <div className="col-md-12 mb-3">
                            <TextAreaField
                                label="Descripción"
                                value={formData.description}
                                onChange={handleInputChange('description')}
                                onFocus={() => onFocusRemove('description')}
                                error={fieldErrorMessage('description')}
                                rows={3}
                                placeholder="Describe brevemente qué incluye este plan..."
                                maxLength={500}
                            />
                            <small className="text-muted">
                                {formData.description.length} / 500 caracteres
                            </small>
                        </div>

                        {/* Características del Plan */}
                        <div className="col-md-12 mt-4 mb-4">
                            <div 
                                className="p-4 rounded-3"
                                style={{ 
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0'
                                }}
                            >
                                <h6 className="mb-2 d-flex align-items-center" style={{ color: '#001849', fontWeight: 600 }}>
                                    <i className="fas fa-list-check me-2 text-primary"></i>
                                    Características del Plan
                                </h6>
                                <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                                    Agrega las características o beneficios que incluye este plan. Se mostrarán al cliente al seleccionar.
                                </p>
                            
                            {/* Input para agregar nueva característica */}
                            <div className="d-flex gap-2 mb-3">
                                <div className="flex-grow-1">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newFeature}
                                        onChange={(e) => setNewFeature(e.target.value)}
                                        onKeyPress={handleFeatureKeyPress}
                                        placeholder="Ej: Análisis completo de viabilidad"
                                        style={{
                                            borderRadius: '8px',
                                            padding: '0.75rem 1rem',
                                            border: '1px solid #dee2e6'
                                        }}
                                    />
                                </div>
                                <ButtonGeneral
                                    onClick={handleAddFeature}
                                    title="Agregar"
                                    variant="primary"
                                    size="small"
                                    icon="fa-plus"
                                    iconPosition="left"
                                    disabled={!newFeature.trim()}
                                />
                            </div>

                            {/* Lista de características */}
                            {formData.features.length > 0 ? (
                                <div 
                                    className="p-3 rounded-3"
                                    style={{ 
                                        background: '#f8f9fa',
                                        border: '1px solid #e9ecef'
                                    }}
                                >
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.features.map((feature, index) => (
                                            <div
                                                key={index}
                                                className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                                                style={{
                                                    background: '#e6f0ff',
                                                    border: '1px solid #001849',
                                                    color: '#001849'
                                                }}
                                            >
                                                <i className="fas fa-check text-success" style={{ fontSize: '0.75rem' }}></i>
                                                <span style={{ fontSize: '0.9rem' }}>{feature}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveFeature(index)}
                                                    className="btn btn-link p-0 ms-1"
                                                    style={{ 
                                                        color: '#dc3545',
                                                        fontSize: '0.85rem',
                                                        lineHeight: 1
                                                    }}
                                                    title="Eliminar característica"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <small className="text-muted d-block mt-2">
                                        {formData.features.length} característica(s) agregada(s)
                                    </small>
                                </div>
                            ) : (
                                <div 
                                    className="text-center py-4 rounded-3"
                                    style={{ 
                                        background: '#f8f9fa',
                                        border: '1px dashed #dee2e6'
                                    }}
                                >
                                    <i className="fas fa-inbox text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                        No hay características agregadas
                                    </p>
                                    <small className="text-muted">
                                        Escribe una característica y presiona Enter o haz clic en Agregar
                                    </small>
                                </div>
                            )}

                            {/* Sugerencias */}
                            <div className="mt-3">
                                <small className="text-muted d-block mb-2">
                                    <i className="fas fa-lightbulb me-1 text-warning"></i>
                                    Ejemplos de características:
                                </small>
                                <div className="d-flex flex-wrap gap-1">
                                    {[
                                        'Análisis de viabilidad',
                                        'Seguimiento del proceso',
                                        'Asesoría legal incluida',
                                        'Soporte prioritario',
                                        'Documentación completa',
                                        'Alertas de marcas similares'
                                    ].map((suggestion, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className="btn btn-sm"
                                            onClick={() => {
                                                if (!formData.features.includes(suggestion)) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        features: [...prev.features, suggestion]
                                                    }));
                                                }
                                            }}
                                            disabled={formData.features.includes(suggestion)}
                                            style={{
                                                background: formData.features.includes(suggestion) ? '#e9ecef' : 'white',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '20px',
                                                fontSize: '0.8rem',
                                                color: formData.features.includes(suggestion) ? '#adb5bd' : '#495057',
                                                cursor: formData.features.includes(suggestion) ? 'not-allowed' : 'pointer'
                                            }}
                                        >
                                            <i className={`fas ${formData.features.includes(suggestion) ? 'fa-check' : 'fa-plus'} me-1`}></i>
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            </div>
                        </div>

                        {/* Lo que NO incluye el Plan */}
                        <div className="col-md-12 mb-4">
                            <div 
                                className="p-4 rounded-3"
                                style={{ 
                                    background: '#fef3f3',
                                    border: '1px solid #f5c2c2'
                                }}
                            >
                                <h6 className="mb-2 d-flex align-items-center" style={{ color: '#721c24', fontWeight: 600 }}>
                                    <i className="fas fa-times-circle me-2 text-danger"></i>
                                    Lo que NO incluye el Plan
                                </h6>
                                <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                                    Especifica claramente lo que NO está incluido en este plan para evitar confusiones.
                                </p>
                            
                                {/* Input para agregar nueva exclusión */}
                                <div className="d-flex gap-2 mb-3">
                                    <div className="flex-grow-1">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newExclusion}
                                            onChange={(e) => setNewExclusion(e.target.value)}
                                            onKeyPress={handleExclusionKeyPress}
                                            placeholder="Ej: Seguimiento post-registro"
                                            style={{
                                                borderRadius: '8px',
                                                padding: '0.75rem 1rem',
                                                border: '1px solid #f5c2c2'
                                            }}
                                        />
                                    </div>
                                    <ButtonGeneral
                                        onClick={handleAddExclusion}
                                        title="Agregar"
                                        variant="danger"
                                        size="small"
                                        icon="fa-plus"
                                        iconPosition="left"
                                        disabled={!newExclusion.trim()}
                                    />
                                </div>

                                {/* Lista de exclusiones */}
                                {formData.exclusions.length > 0 ? (
                                    <div 
                                        className="p-3 rounded-3"
                                        style={{ 
                                            background: '#fff5f5',
                                            border: '1px solid #f5c2c2'
                                        }}
                                    >
                                        <div className="d-flex flex-wrap gap-2">
                                            {formData.exclusions.map((exclusion, index) => (
                                                <div
                                                    key={index}
                                                    className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                                                    style={{
                                                        background: '#ffe6e6',
                                                        border: '1px solid #dc3545',
                                                        color: '#721c24'
                                                    }}
                                                >
                                                    <i className="fas fa-times text-danger" style={{ fontSize: '0.75rem' }}></i>
                                                    <span style={{ fontSize: '0.9rem' }}>{exclusion}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveExclusion(index)}
                                                        className="btn btn-link p-0 ms-1"
                                                        style={{ 
                                                            color: '#721c24',
                                                            fontSize: '0.85rem',
                                                            lineHeight: 1
                                                        }}
                                                        title="Eliminar exclusión"
                                                    >
                                                        <i className="fas fa-times-circle"></i>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <small className="text-muted d-block mt-2">
                                            {formData.exclusions.length} exclusión(es) agregada(s)
                                        </small>
                                    </div>
                                ) : (
                                    <div 
                                        className="text-center py-4 rounded-3"
                                        style={{ 
                                            background: '#fff5f5',
                                            border: '1px dashed #f5c2c2'
                                        }}
                                    >
                                        <i className="fas fa-ban text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                                            No hay exclusiones agregadas
                                        </p>
                                        <small className="text-muted">
                                            Agrega lo que NO está incluido en este plan
                                        </small>
                                    </div>
                                )}

                                {/* Sugerencias de exclusiones */}
                                <div className="mt-3">
                                    <small className="text-muted d-block mb-2">
                                        <i className="fas fa-lightbulb me-1 text-warning"></i>
                                        Ejemplos de exclusiones:
                                    </small>
                                    <div className="d-flex flex-wrap gap-1">
                                        {[
                                            'Seguimiento post-registro',
                                            'Defensa ante oposiciones',
                                            'Renovación de marca',
                                            'Registro internacional',
                                            'Tasas gubernamentales',
                                            'Monitoreo continuo'
                                        ].map((suggestion, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={() => {
                                                    if (!formData.exclusions.includes(suggestion)) {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            exclusions: [...prev.exclusions, suggestion]
                                                        }));
                                                    }
                                                }}
                                                disabled={formData.exclusions.includes(suggestion)}
                                                style={{
                                                    background: formData.exclusions.includes(suggestion) ? '#f8d7da' : 'white',
                                                    border: '1px solid #f5c2c2',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    color: formData.exclusions.includes(suggestion) ? '#adb5bd' : '#721c24',
                                                    cursor: formData.exclusions.includes(suggestion) ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                <i className={`fas ${formData.exclusions.includes(suggestion) ? 'fa-check' : 'fa-plus'} me-1`}></i>
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Vista previa del plan */}
                    {formData.plan_name && formData.base_price && !isNaN(Number(formData.base_price)) && (
                        <div 
                            className="p-4 rounded-3 mb-4"
                            style={{
                                background: 'linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%)',
                                border: '2px solid #001849'
                            }}
                        >
                            <h6 className="mb-3" style={{ color: '#001849' }}>
                                <i className="fas fa-eye me-2"></i>
                                Vista Previa del Plan
                            </h6>
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                    <span className="badge bg-secondary mb-2">
                                        {serviceTypeOptions.find(s => s.value === formData.service_type)?.label}
                                    </span>
                                    <h5 className="mb-1" style={{ color: '#001849', fontWeight: 600 }}>
                                        {formData.plan_name}
                                        {formData.is_default && (
                                            <span className="badge bg-primary ms-2" style={{ fontSize: '0.7rem' }}>
                                                Por defecto
                                            </span>
                                        )}
                                    </h5>
                                    {formData.description && (
                                        <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>
                                            {formData.description}
                                        </p>
                                    )}
                                    {formData.features.length > 0 && (
                                        <ul className="list-unstyled mb-0 mt-3">
                                            {formData.features.map((feature, idx) => (
                                                <li key={idx} className="mb-1" style={{ fontSize: '0.85rem' }}>
                                                    <i className="fas fa-check text-success me-2"></i>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {formData.exclusions.length > 0 && (
                                        <ul className="list-unstyled mb-0 mt-3">
                                            {formData.exclusions.map((exclusion, idx) => (
                                                <li key={idx} className="mb-1" style={{ fontSize: '0.85rem', color: '#dc3545' }}>
                                                    <i className="fas fa-times text-danger me-2"></i>
                                                    {exclusion}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="text-end ms-4">
                                    <span style={{ fontSize: '1.75rem', fontWeight: 700, color: '#001849' }}>
                                        ${Number(formData.base_price).toLocaleString('es-CL')}
                                    </span>
                                    <span className="text-muted ms-1">{formData.currency}</span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Botones */}
                    <div className="d-flex gap-2 justify-content-end mt-4">
                        <ButtonCancelForm callbackCancel={handleCancel} />
                        <ButtonSaveForm
                            callbackSave={handleSubmit}
                            loading={isSaving}
                            title={isEditing ? "Actualizar" : "Crear Plan"}
                        />
                    </div>
                </form>
            </DefaultCard>
        </div>
    );
};

export default ServicePricingForm;
